import { api } from "@bindery/convex-reader/api";
import { useQuery } from "convex/react";
import { useState } from "react";
import { StatCard } from "./stat-card";
import { FluencyProgress } from "./fluency-progress";
import { RecentActivity } from "./recent-activity";
import {
  BookOpen,
  Star,
  CheckCircle,
  TrendingUp,
  Flame,
  Timer,
} from "lucide-react";
import { getBookTitleOrDefault } from "../default-library";

function getStreakColor(streak: number): string {
  if (streak >= 7) return "text-flamenco-600";
  if (streak >= 3) return "text-flamenco-500";
  return "text-muted-foreground/60";
}

function getWpmColor(improvement: number): string {
  if (improvement > 0) return "text-mintleaf-600";
  if (improvement < 0) return "text-flamenco-500";
  return "text-muted-foreground/60";
}

export function localMidnight(now: Date): number {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}

export function ReadingStats() {
  // Fixed at mount: a new dayStart would be a new subscription.
  const [renderedAt] = useState(Date.now);
  const stats = useQuery(api.stats.mine, {
    dayStart: localMidnight(new Date(renderedAt)),
  });

  if (!stats) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="bg-muted h-24 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Books Started"
          value={stats.booksStarted}
          icon={BookOpen}
          iconColor="text-skyfall-600"
        />
        <StatCard
          title="Completed"
          value={stats.booksCompleted}
          subtitle={`${stats.completionRate.toFixed(0)}% completion rate`}
          icon={CheckCircle}
          iconColor="text-mintleaf-600"
        />
        <StatCard
          title="Average Rating"
          value={
            stats.averageRating > 0 ? `${stats.averageRating.toFixed(1)}` : "—"
          }
          subtitle={
            stats.totalRatings > 0
              ? `${stats.totalRatings} book${stats.totalRatings > 1 ? "s" : ""} rated`
              : "No ratings yet"
          }
          icon={Star}
          iconColor="text-flamenco-500"
        />
        <StatCard
          title="Words Mastered"
          value={stats.wordsMastered}
          subtitle={`${stats.totalWords} total words encountered`}
          icon={TrendingUp}
          iconColor="text-skyfall-600"
        />
        <StatCard
          title="Reading Days"
          value={stats.readingDays}
          subtitle={`Longest streak: ${stats.longestStreak} days`}
          icon={BookOpen}
          iconColor="text-plumwood-600"
        />
        <StatCard
          title="Current Streak"
          value={stats.currentStreak > 0 ? `${stats.currentStreak} days` : "—"}
          subtitle={
            stats.currentStreak > 0 ? "Keep it going!" : "Start reading!"
          }
          icon={Flame}
          iconColor={getStreakColor(stats.currentStreak)}
        />
      </div>

      {stats.averageWpm !== null && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StatCard
            title="Reading Rate"
            value={`${stats.averageWpm} WPM`}
            subtitle="Average over last 5 days"
            icon={Timer}
            iconColor="text-skyfall-600"
          />
          {stats.wpmImprovement !== null && (
            <StatCard
              title="Rate Improvement"
              value={`${stats.wpmImprovement > 0 ? "+" : ""}${stats.wpmImprovement}%`}
              subtitle="Compared to 4 weeks ago"
              icon={TrendingUp}
              iconColor={getWpmColor(stats.wpmImprovement)}
            />
          )}
        </div>
      )}

      {stats.totalWords > 0 && (
        <FluencyProgress
          wordsByFluency={stats.wordsByFluency}
          totalWords={stats.totalWords}
        />
      )}

      <RecentActivity
        stats={{
          ...stats,
          recentCompletions: stats.recentCompletions.map((completion) => ({
            ...completion,
            title: getBookTitleOrDefault(completion.readingId),
          })),
        }}
      />
      {(stats.truncated.fluency || stats.truncated.learningRecords) && (
        <p className="text-muted-foreground text-xs">
          Counted from your most recent reading; older activity is not included.
        </p>
      )}
    </div>
  );
}
